<template>
  <table-wrapper>
    <div class="coronet-table--loader">
      <v-progress-linear
        v-if="loading"
        data-testid="table-progress-bar"
        indeterminate
        height="2px"
      ></v-progress-linear>
    </div>
    <v-data-table-server
      class="coronet-table--highlightable"
      :fixed-header="true"
      :items="items"
      :headers="headers"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      item-key="groupId"
      show-select
      return-object
      v-model="selectedItems"
      @update:options="
        $emit('page-changed', { pageSize: $event.itemsPerPage, page: $event.page - 1 })
      "
    >
      <!--Slot for table header-->
      <template #headers="{ columns }">
        <tr>
          <th
            v-for="header of columns"
            :key="header.key as string"
            :class="{
              'checkbox-col': header.value === 'data-table-select',
            }"
            :aria-label="
              header.value === 'data-table-select'
                ? $t('ariaLabels.checkboxColumn')
                : $t(`usersSettings.userGroupsTab.tableHeaders.${header.key}`)
            "
          >
            <bulk-selector-checkbox
              v-if="header.key === 'data-table-select'"
              :is-indeterminate="isHeaderIndeterminate"
              :all-items-on-page-selected="allItemsOnPageSelected"
              :items="items"
              :show-menu="false"
              :all-items-selected="isHeaderChecked"
              @selection-changed="onSelectionChanged"
            />
            <template v-else-if="header.key === 'groupName' && selectedItemsQuantity > 0">
              <span class="table-selected-text" data-testid="user-groups-selected-groups-text">
                {{
                  $t("usersSettings.userGroupsTab.groupsSelected", {
                    n: selectedItemsQuantity,
                  })
                }}
              </span>
              <v-menu v-if="bulkActions.length > 0" bottom left>
                <template #activator="{ props: menuProps, isActive }">
                  <v-btn
                    rounded
                    color="primary"
                    class="ml-2 text-uppercase"
                    v-bind="menuProps"
                    data-testid="user-groups-table-actions-btn"
                  >
                    {{ $t("usersSettings.userGroupsTab.actionsBtn") }}
                    <v-icon
                      class="ml-2"
                      size="24"
                      icon="$triangle"
                      :class="{ rotated: isActive }"
                    />
                  </v-btn>
                </template>
                <v-list>
                  <template v-for="action in bulkActions" :key="action">
                    <v-list-item @click="$emit('bulk-action-applied', action)">
                      <v-list-item-title
                        :data-testid="`user-groups-table-${kebabCase(action)}-btn-text`"
                      >
                        {{ $t(`usersSettings.userGroupsTab.actions.${action}`) }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </template>
            <span v-else :data-testid="`table-${header.key}-header`">
              {{ $t(`usersSettings.userGroupsTab.tableHeaders.${header.key}`) }}
            </span>
          </th>
          <th class="text-center" :aria-label="$t('ariaLabels.actionsColumn')"></th>
        </tr>
      </template>
      <!--Slot for rows-->
      <template #item="{ item, isSelected, internalItem, index }">
        <tr
          :data-testid="`table-row-${index}`"
          :class="{ 'coro-table-row--selected': isSelected([internalItem]) }"
        >
          <td class="checkbox-col" @click="$event.stopPropagation()">
            <v-checkbox-btn
              :data-testid="`table-checkbox-row-${index}`"
              :model-value="isSelected([internalItem])"
              @update:modelValue="handleOneRowSelection($event, item)"
              @click.stop
            ></v-checkbox-btn>
          </td>
          <td class="w-60" :data-testid="`user-groups-group-name-col-row-${index}`">
            <div class="d-flex align-center">
              <coro-icon class="service-icon" :icon-name="item.service"></coro-icon>
              <b class="ml-1 mr-2" :data-testid="`user-groups-table-group-name-row-${index}`">{{
                item.name
              }}</b>
            </div>
          </td>
          <td class="w-15" :data-testid="`user-groups-members-col-row-${index}`">
            <router-link
              class="coro-link"
              :to="{
                name: RouteName.USERS_SETTINGS_USERS_TAB,
              }"
              >{{ item.membersCount }}</router-link
            >
          </td>
          <td class="w-15" :data-testid="`user-groups-members-col-row-${index}`">
            <div :class="protectedStatusColor(item)" v-if="item.protectedStatus">
              {{
                $t(`usersSettings.userGroupsTab.protectionStatus.${item.protectedStatus}`, {
                  n: item.protectedUsersCount + "/" + item.membersCount,
                })
              }}
            </div>
          </td>
          <td class="w-15" :data-testid="`user-groups-protection-granted-col-row-${index}`">
            {{ getFormattedDateTime(item.protectionGrantedTime, "ddd, MMM D YYYY, hh:mm A") }}
          </td>
          <td
            class="w-5 coro-table-col--sticky-right"
            :data-testid="`user-groups-action-col-row-${index}`"
            @click="$event.stopPropagation()"
          >
            <v-menu bottom left>
              <template #activator="{ props }">
                <v-icon
                  :data-testid="`user-groups-action-btn-row-${index}`"
                  icon="$dots"
                  v-bind="props"
                />
              </template>

              <v-list>
                <v-list-item
                  v-for="action in getItemActions(item)"
                  :key="action"
                  @click="$emit('item-action-applied', { action, item })"
                >
                  <v-list-item-title :data-testid="`user-groups-table-${action}-action`">
                    {{ $t(`usersSettings.userGroupsTab.actions.${action}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
      <template #no-data>
        <v-icon class="no-items-icon mt-3 mb-3" size="80" icon="$users" />
        <div class="headline5 mb-3">
          {{ $t("usersSettings.userGroupsTab.noGroups") }}
        </div>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { getFormattedDateTime } from "@/_helpers/date";
import TableWrapper from "@/components/TableWrapper.vue";
import { computed, defineComponent, type PropType, toRefs } from "vue";
import CoroIcon from "@/components/CoroIcon.vue";
import { GroupProtectionStatus, UserGroupAction } from "@/constants/users";
import type { Pagination } from "@/types";
import type { UserGroupSettingsItem } from "@/_store/users-settings/user-groups-tab.module";
import { useSelectableTable } from "@/composables/useSelectableTable";
import BulkSelectorCheckbox from "@/components/BulkSelectorCheckbox.vue";
import { RouteName } from "@/constants/routes.ts";
import kebabCase from "lodash/kebabCase";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  components: {
    BulkSelectorCheckbox,
    TableWrapper,
    CoroIcon,
  },
  props: {
    items: {
      type: Array as PropType<UserGroupSettingsItem[]>,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => {
        return {
          page: 0,
          pageSize: 15,
        };
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["page-changed", "item-action-applied", "bulk-action-applied"],
  setup(props) {
    const { totalItems, items } = toRefs(props);

    const actionNotAllowed = computed(() => {
      return isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.WORKSPACE_MANAGEMENT,
        WorkspaceManagementScopeSections.USERS
      );
    });

    const {
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsOnPageSelected,
      allItemsSelected,
      selectedItemsQuantity,
    } = useSelectableTable({
      totalItems,
      items,
    });

    const isHeaderChecked = computed(() => allItemsSelected.value || allItemsOnPageSelected.value);

    const isHeaderIndeterminate = computed(
      () => !isHeaderChecked.value && isIndeterminate.value && selectedItemsQuantity.value > 0
    );

    const bulkActions = computed(() => {
      if (!selectedItems.value.length) return [];
      const actionsPerItem = selectedItems.value.map((item) => getItemActions(item));
      return actionsPerItem.reduce((common, actions) =>
        common.filter((action) => actions.includes(action))
      );
    });

    const protectedStatusColor = (item: UserGroupSettingsItem) => {
      switch (item.protectedStatus) {
        case GroupProtectionStatus.PROTECTED:
          return { "text-green-dark": true };
        case GroupProtectionStatus.UNPROTECTED:
          return { "text-red-dark": true };
        case GroupProtectionStatus.PARTIALLY_PROTECTED:
          return { "text-yellow-base": true };
      }
    };

    const getItemActions = (item: UserGroupSettingsItem): UserGroupAction[] => {
      switch (item.protectedStatus) {
        case GroupProtectionStatus.PROTECTED:
          return [UserGroupAction.DISABLE_GROUP_PROTECTION];
        case GroupProtectionStatus.UNPROTECTED:
          return [UserGroupAction.ENABLE_GROUP_PROTECTION];
        case GroupProtectionStatus.PARTIALLY_PROTECTED:
          return [
            UserGroupAction.ENABLE_GROUP_PROTECTION,
            UserGroupAction.DISABLE_GROUP_PROTECTION,
          ];
        default:
          return [];
      }
    };

    return {
      headers: [
        { key: "groupName", sortable: false },
        { key: "size", sortable: false },
        { key: "protectionStatus", sortable: false },
        { key: "protectionGrantedTime", sortable: false },
      ],
      userGroupActions: UserGroupAction,
      GroupProtectionStatus,
      RouteName,
      kebabCase,
      protectedStatusColor,
      getItemActions,
      actionNotAllowed,
      bulkActions,
      selectedItems,
      allItemsSelected,
      handleOneRowSelection,
      onSelectionChanged,
      getFormattedDateTime,
      isIndeterminate,
      isHeaderChecked,
      isHeaderIndeterminate,
      allItemsOnPageSelected,
      selectedItemsQuantity,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-selection-control__wrapper {
    width: 22px !important;
  }
}

.service-icon {
  height: 30px;
  width: 30px;
}

.table-selected-text {
  font-size: 16px;
  font-weight: 500;
}

.v-btn:not(.text-uppercase) {
  text-transform: unset !important;
}

.checkbox-col {
  min-width: 65px !important;
}
</style>
